import _extends from '@babel/runtime/helpers/extends';
import _typeof from '@babel/runtime/helpers/typeof';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { Box } from '../Box/Box.js';
import { Icon } from '../Icon/Icon.js';
import { TextLink } from '../TextLink/TextLink.js';
import { useExperimentContext } from '../../contexts/session/Experiment.js';
import { actionElement, iconElement, titleElement, descriptionElement } from './BaseNotification.js';
import { defaultProps, getTokens, getDensityProps } from './Notification.mappers.js';

var _excluded = ["title", "description", "closeButton", "closeButtonProps", "action", "actions", "onClose", "actionUrl", "actionTarget", "intent", "radius", "asset", "onActionClick", "density"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Inline notifications are like road signs on a motorcycle trip - they provide timely and relevant information and alerts to keep users on track without disrupting their journey. So whether you’re warning users about a detour or guiding them to their destination, inline notifications are there to help navigate the way.
 */
var Notification = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var propsWithDefault = _objectSpread(_objectSpread({}, defaultProps), props);
  var title = propsWithDefault.title,
    description = propsWithDefault.description,
    closeButton = propsWithDefault.closeButton,
    closeButtonProps = propsWithDefault.closeButtonProps,
    action = propsWithDefault.action,
    actions = propsWithDefault.actions,
    onClose = propsWithDefault.onClose,
    actionUrl = propsWithDefault.actionUrl,
    actionTarget = propsWithDefault.actionTarget,
    intent = propsWithDefault.intent,
    radius = propsWithDefault.radius,
    asset = propsWithDefault.asset,
    onActionClick = propsWithDefault.onActionClick,
    density = propsWithDefault.density,
    rest = _objectWithoutProperties(propsWithDefault, _excluded);
  var experimentContext = useExperimentContext();
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isClosed = _React$useState2[0],
    setClosed = _React$useState2[1];
  var _React$useMemo = React.useMemo(function () {
      return getTokens(intent, experimentContext);
    }, [intent, experimentContext]),
    background = _React$useMemo.background,
    text = _React$useMemo.text;
  var actionElement$1 = React.useMemo(function () {
    if (!action) return null;
    return /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      flexShrink: 1,
      justifyContent: "flex-end",
      alignItems: "end",
      width: "100%",
      order: 1,
      pt: "8"
    }, /*#__PURE__*/React.createElement(TextLink, {
      onClick: onActionClick,
      hierarchy: "primary",
      textVariant: "body-s",
      label: action,
      href: actionUrl,
      target: actionTarget
    }));
  }, [action, actionUrl, actionTarget, onActionClick]);
  var actionsElements = React.useMemo(function () {
    if (!actions) {
      return null;
    }
    return /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      flexShrink: 1,
      justifyContent: "flex-end",
      alignItems: "end",
      width: "100%",
      order: 1,
      gap: "12"
    }, actions.map(actionElement));
  }, [actions]);
  var closeElement = React.useMemo(function () {
    if (!closeButton && !closeButtonProps) return null;
    var closeButtonPropsDefault = closeButtonProps || {
      'aria-label': 'close-button'
    };
    var handleClose = function handleClose() {
      if ((onClose === null || onClose === void 0 ? void 0 : onClose()) === false) return;
      setClosed(true);
    };
    return /*#__PURE__*/React.createElement(Box, _extends({
      float: "end",
      onClick: handleClose,
      cursor: "pointer",
      as: "button",
      p: "8",
      m: "-8",
      display: "flex"
    }, closeButtonPropsDefault, _typeof(closeButton) === 'object' ? closeButton : {}), /*#__PURE__*/React.createElement(Icon, {
      id: "fill.symbol.close",
      size: "16"
    }));
  }, [closeButton, closeButtonProps, onClose]);
  if (isClosed) return null;
  return /*#__PURE__*/React.createElement(Box, _extends({
    ref: ref,
    role: "alert",
    "aria-live": intent !== 'error' ? 'polite' : 'assertive',
    display: "flex",
    alignItems: "stretch",
    overflow: "hidden",
    backgroundColor: background,
    textColor: text
  }, getDensityProps(density, radius), rest), iconElement(asset === null || asset === void 0 ? void 0 : asset.id, text), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexGrow: 1,
    flexWrap: "wrap",
    alignItems: "center",
    qM: {
      flexWrap: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    flexGrow: 1,
    flexBasis: 0
  }, closeElement, titleElement(title, description, !!closeButton), descriptionElement(description), actionElement$1 || actionsElements)));
});
Notification.displayName = 'Notification';

export { Notification };
//# sourceMappingURL=Notification.js.map
